import type { IntlShape } from 'react-intl'

export interface FlightDisplayData {
  departureDate?: string
  arrivalDate?: string
  departureLabel?: string
  arrivalLabel?: string
  airlineLogoUrl?: string
  isReturnFlight: boolean
  isDirectFlight: boolean
}

export function deriveFlightDisplayData(flightDetails?: App.FlightOrderItemDetails): FlightDisplayData {
  const legs = flightDetails?.bookingJourney?.legs ?? []
  const allFlights = legs.flatMap(leg => leg.flights ?? [])
  const firstFlight = allFlights[0]
  const lastFlight = allFlights.at(-1)
  const outboundFlights = legs[0]?.flights ?? []
  const outboundLastFlight = outboundFlights.at(-1)

  const isReturnFlight = Boolean(
    firstFlight?.departureAirport &&
    lastFlight?.arrivalAirport &&
    firstFlight.departureAirport === lastFlight.arrivalAirport &&
    legs.length > 1,
  )

  const destinationFlight = isReturnFlight ? outboundLastFlight : lastFlight

  return {
    departureDate: firstFlight?.departingDate,
    arrivalDate: lastFlight?.arrivalDate ?? firstFlight?.departingDate,
    departureLabel: firstFlight?.departureCity ?? firstFlight?.departureAirportName ?? firstFlight?.departureAirport,
    arrivalLabel: destinationFlight?.arrivalCity ?? destinationFlight?.arrivalAirportName ?? destinationFlight?.arrivalAirport,
    airlineLogoUrl: firstFlight?.marketingAirline?.brandMarkUrl,
    isReturnFlight,
    isDirectFlight: !isReturnFlight && outboundFlights.length === 1,
  }
}

export function formatFlightFallbackTitle(intl: IntlShape): string {
  return intl.formatMessage({
    id: 'oi1krqKJZJpTFmIwMckY',
    defaultMessage: 'Flight booking',
    description: 'Fallback title for a booked flight whose route is unknown, on trip planner chat booking tiles and the chat landing page upcoming trips',
  })
}

export function formatFlightTypeLabel(intl: IntlShape, isReturnFlight: boolean, isDirectFlight: boolean): string {
  if (isReturnFlight) {
    return intl.formatMessage({
      id: 'EBXs5a3nf9rTLsrSBibT',
      defaultMessage: 'Return',
      description: 'Flight type label for a booked return flight, on trip planner chat booking tiles and the chat landing page upcoming trips',
    })
  }

  if (isDirectFlight) {
    return intl.formatMessage({
      id: 'sm60_9cGBiowcxZuCMTa',
      defaultMessage: 'Direct',
      description: 'Flight type label for a booked direct one-way flight, on trip planner chat booking tiles and the chat landing page upcoming trips',
    })
  }

  return intl.formatMessage({
    id: 'HIYvyowSg7n2SszAMlmU',
    defaultMessage: 'One-way',
    description: 'Flight type label for a booked one-way flight with connections, on trip planner chat booking tiles and the chat landing page upcoming trips',
  })
}

export function formatFlightRouteLabel(intl: IntlShape, departureLabel?: string, arrivalLabel?: string): string {
  if (!departureLabel || !arrivalLabel) {
    return formatFlightFallbackTitle(intl)
  }

  return intl.formatMessage(
    {
      id: 'bREzusYYplzYcftZm3N3',
      defaultMessage: '{origin} to {destination}',
      description: 'Flight route naming the origin and destination, used as the title of a flight booking row on the TripPlanner chat landing, in the plan-around-trip chat message and in the booking tag attached to the chat composer',
    },
    {
      origin: departureLabel,
      destination: arrivalLabel,
    },
  )
}
